/**
 * reservationsV2Store — estado runtime propio de la vista previa "Reservas V2".
 *
 * Mismo patrón que `pmsRuntimeStore`, `settingsV2Store` y `todayV2Store`: un
 * módulo, `useSyncExternalStore`, cero persistencia, cero red. Al recargar
 * vuelve el dataset semilla.
 *
 * Reparto de responsabilidades, para no tener la misma verdad en dos sitios:
 *   - La RESERVA (fechas, canal, tres ejes de estado, unidad asignada,
 *     cancelación) vive en `pmsRuntimeStore`, que es el dataset que comparten
 *     el calendario, el reparto y Hoy. Cambiarla allí mantiene coherentes
 *     todas las pantallas.
 *   - Las PETICIONES y los PEDIDOS DE SERVICIO viven en `todayV2Store`, de
 *     donde ya los lee Hoy.
 *   - Aquí viven solo los eventos de pago y el registro de actividad, que son
 *     conceptos que el modelo V1 todavía no tiene.
 */
import { useSyncExternalStore } from "react";
import { RESERVATION_EXTRAS } from "@/data/reservationsV2Demo";

function seed() {
  const paymentEvents = {};
  for (const [reservationId, extras] of Object.entries(RESERVATION_EXTRAS)) {
    if (!extras.payment_events) continue;
    paymentEvents[reservationId] = extras.payment_events.map((e, i) => ({
      event_id: `PE-${reservationId}-${i + 1}`,
      origin: "seed",
      ...e,
    }));
  }
  return { paymentEvents, activity: {}, tick: 0 };
}

let state = seed();
const listeners = new Set();

function emit(next) {
  state = next;
  for (const fn of listeners) fn();
}

/** Toda mutación pasa por aquí, así el aviso de "actualizado" es automático. */
function commit(patch) {
  emit({ ...state, ...patch, tick: state.tick + 1 });
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ---------------------------------------------------------------------------
// Lecturas
// ---------------------------------------------------------------------------

export function getReservationsV2() {
  return state;
}

export function useReservationsV2() {
  return useSyncExternalStore(subscribe, getReservationsV2);
}

/** Solo para tests: vuelve al dataset semilla. */
export function resetReservationsV2() {
  emit(seed());
}

// ---------------------------------------------------------------------------
// Mutaciones (todas locales a la sesión)
// ---------------------------------------------------------------------------

let paymentCounter = 0;

/**
 * Registra un evento de pago de la vista previa. NO cambia el estado de pago
 * de la reserva: son ejes distintos y quien decide el estado es una persona
 * (§8). Aquí solo queda anotado el movimiento.
 */
export function addPaymentEvent(reservationId, payload) {
  paymentCounter += 1;
  const event = {
    event_id: `PE-SESSION-${String(paymentCounter).padStart(3, "0")}`,
    at: payload.at || null,
    kind: payload.kind || "deposit",
    amount_eur: Number(payload.amount_eur || 0),
    method: payload.method || "other",
    note: payload.note || null,
    origin: "created_in_session",
  };
  const current = state.paymentEvents[reservationId] || [];
  commit({
    paymentEvents: { ...state.paymentEvents, [reservationId]: [...current, event] },
  });
  return event;
}

let activityCounter = 0;

/**
 * Anota algo en el registro de actividad. Importa el concepto: más adelante un
 * operador tiene que poder ver quién cambió qué y cuándo.
 */
export function logActivity(reservationId, entry) {
  activityCounter += 1;
  const record = {
    entry_id: `AC-SESSION-${String(activityCounter).padStart(3, "0")}`,
    kind: entry.kind || "status",
    detail: entry.detail || null,
    at: entry.at || null,
    actor: entry.actor || "Dolce Admin",
    origin: "created_in_session",
  };
  const current = state.activity[reservationId] || [];
  commit({
    activity: { ...state.activity, [reservationId]: [...current, record] },
  });
  return record;
}
